/**
 * useMultiTechAutoOptimization Hook
 *
 * Handles automatic route optimization for multiple technicians when viewing the "all" tab.
 * Processes optimization sequentially to avoid rate limiting.
 *
 * Features:
 * - Auto-triggers optimization for each technician with 'unsorted' or 'auto_stale' routes
 * - Processes sequentially (not in parallel) to avoid API rate limits
 * - Tracks progress with completed/total counts
 * - Provides progress info for UI display
 */

import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Workorder } from '@certhon/models';
import {
  calculateRoutingKey,
  getRouteStatus,
  canAutoSort as canAutoSortUtil,
} from '@certhon/shared';
import { toast } from 'sonner';
import { optimizeRoute } from '../../dayplanning/dayplanning.api';
import { dayPlanningQueries } from '../../dayplanning/dayplanning.queries';
import { HTTP_STATUS } from '../constants';

/**
 * Information about a technician that needs optimization
 */
interface TechnicianToOptimize {
  techId: number;
  workorders: Workorder[];
}

/**
 * Progress info for multi-tech optimization
 */
export interface MultiTechOptimizationProgress {
  /** Number of technicians that have been optimized */
  completed: number;
  /** Total number of technicians that need optimization */
  total: number;
  /** Whether optimization is currently in progress */
  isOptimizing: boolean;
  /** Whether all optimizations are complete */
  isComplete: boolean;
}

/**
 * Options for the useMultiTechAutoOptimization hook
 */
export interface UseMultiTechAutoOptimizationOptions {
  /** Map of technician ID to their workorders */
  workordersByTech: Map<number, Workorder[]> | undefined;
  /** The date being planned */
  date: string;
  /** Whether user has admin permissions */
  isAdmin: boolean;
  /** Whether this view is currently active (to control when optimization triggers) */
  isActive: boolean;
}

/**
 * Return value from useMultiTechAutoOptimization hook
 */
export interface UseMultiTechAutoOptimizationResult {
  /** Progress information for the optimization batch */
  progress: MultiTechOptimizationProgress;
  /** Whether any error occurred during optimization */
  hasError: boolean;
  /** The error that occurred (if any) */
  error: Error | null;
  /** Manually trigger optimization for all technicians that need it */
  optimizeAll: () => void;
}

/**
 * Calculate routing status for a single technician's workorders
 */
function getTechnicianRoutingStatus(workorders: Workorder[], isAdmin: boolean) {
  if (!workorders || workorders.length === 0) {
    return { needsOptimization: false };
  }

  // Calculate current routing key
  const currentKey = calculateRoutingKey(
    workorders.map((wo) => ({
      recnum: wo.recnum,
      location_address: wo.location_address,
      route_option: wo.route_option,
    })),
  );

  // Get stored values from first workorder
  const storedKey = workorders[0]?.routing_key ?? null;
  const sortOrderSource = workorders[0]?.sort_order_source ?? null;

  // Determine route status
  const status = getRouteStatus(sortOrderSource, storedKey, currentKey);
  const canAutoSort = canAutoSortUtil(status, isAdmin);

  // Check if this technician needs optimization
  const needsOptimization =
    canAutoSort && (status === 'unsorted' || status === 'auto_stale');

  return { needsOptimization, status };
}

/**
 * Hook for managing automatic route optimization across multiple technicians.
 *
 * This hook:
 * 1. Identifies technicians whose routes need optimization
 * 2. Processes them sequentially to avoid rate limits
 * 3. Provides progress tracking for UI display
 */
export function useMultiTechAutoOptimization({
  workordersByTech,
  date,
  isAdmin,
  isActive,
}: UseMultiTechAutoOptimizationOptions): UseMultiTechAutoOptimizationResult {
  const queryClient = useQueryClient();

  // Track optimization state
  const [queue, setQueue] = useState<TechnicianToOptimize[]>([]);
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Ref to prevent double initialization in React Strict Mode
  const initTriggered = useRef(false);
  const currentProcessingRef = useRef<number | null>(null);

  // Mutation for optimizing a single technician's route
  const optimizeMutation = useMutation({
    mutationFn: async (techId: number) => {
      return optimizeRoute(date, techId);
    },
    onError: (err: Error) => {
      if (
        (err as { status?: number }).status === HTTP_STATUS.forbidden ||
        err.message?.toLowerCase().includes('permission')
      ) {
        toast.error('Permission denied', {
          description:
            'You need coordinator access (WB_ADMIN) to optimize routes.',
        });
      }
      setError(err);
    },
    onSettled: (_, __, techId) => {
      // Invalidate queries for this technician
      queryClient.invalidateQueries({
        queryKey: dayPlanningQueries.day(techId, date).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: dayPlanningQueries.allTechnicians(date).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: [...dayPlanningQueries.all(), 'routes'],
      });
    },
  });

  // Find technicians that need optimization
  const techniciansToOptimize = useMemo(() => {
    if (!workordersByTech || workordersByTech.size === 0 || !isAdmin) {
      return [];
    }

    const toOptimize: TechnicianToOptimize[] = [];

    for (const [techId, workorders] of workordersByTech.entries()) {
      if (workorders.length === 0) continue;

      const { needsOptimization } = getTechnicianRoutingStatus(
        workorders,
        isAdmin,
      );
      if (needsOptimization) {
        toOptimize.push({ techId, workorders });
      }
    }

    return toOptimize;
  }, [workordersByTech, isAdmin]);

  // Process the queue - optimize one technician at a time
  const processQueue = useCallback(async () => {
    if (queue.length === 0) {
      setIsOptimizing(false);
      return;
    }

    const [next, ...rest] = queue;
    currentProcessingRef.current = next.techId;

    try {
      await optimizeMutation.mutateAsync(next.techId);
      setCompleted((c) => c + 1);
    } catch {
      // Error already handled in mutation onError
    }

    currentProcessingRef.current = null;
    setQueue(rest);
  }, [queue, optimizeMutation]);

  // Effect to process queue sequentially
  useEffect(() => {
    if (
      isOptimizing &&
      queue.length > 0 &&
      !optimizeMutation.isPending &&
      currentProcessingRef.current === null
    ) {
      processQueue();
    }

    // Check if complete
    if (isOptimizing && queue.length === 0 && !optimizeMutation.isPending) {
      setIsOptimizing(false);
    }
  }, [isOptimizing, queue, optimizeMutation.isPending, processQueue]);

  // Auto-start optimization when conditions are met
  useEffect(() => {
    if (
      isActive &&
      techniciansToOptimize.length > 0 &&
      !isOptimizing &&
      !initTriggered.current
    ) {
      initTriggered.current = true;
      setQueue(techniciansToOptimize);
      setTotal(techniciansToOptimize.length);
      setCompleted(0);
      setError(null);
      setIsOptimizing(true);
    }
  }, [isActive, techniciansToOptimize, isOptimizing]);

  // Reset trigger when view becomes inactive
  useEffect(() => {
    if (!isActive) {
      initTriggered.current = false;
    }
  }, [isActive]);

  // Manual trigger function
  const optimizeAll = useCallback(() => {
    if (techniciansToOptimize.length === 0) return;

    setQueue(techniciansToOptimize);
    setTotal(techniciansToOptimize.length);
    setCompleted(0);
    setError(null);
    setIsOptimizing(true);
  }, [techniciansToOptimize]);

  const isComplete = !isOptimizing && queue.length === 0 && total > 0;

  return {
    progress: {
      completed,
      total,
      isOptimizing,
      isComplete,
    },
    hasError: error !== null,
    error,
    optimizeAll,
  };
}
